<template>
  <MainLayout>
    <news-header>
      <template #user>
        <UserWidget
            :source-lang="state.nativeLanguage"
            :target-lang="state.learnedLanguage"/>
      </template>
    </news-header>
    <news-menu :current-category-id="state.selectedCategoryId || state.article?.title.category"/>
    <transition name="fade" mode="out-in" :duration="{ enter: 250, leave: 80 }">
      <ContentArea
          :category-title="state.categoryTitleForHeader"
          :category-id="articleCategoryId"
          @dailyClick="navigateToDailyWords"
          @collectionClick="() => viewModel.navigateToWordCollection()"
          @repeatClick="() => viewModel.navigateToRepeat()">
        <template #headerTranslit v-if="showHeaderTranslitToggle">
          <button
            type="button"
            class="pill-btn"
            :class="{ active: globalTransliterateEnabled }"
            @click="toggleGlobalTransliteration"
          >
            <img :src="translateIconUrl" alt="" width="18" height="18" class="translit-icon" />
            {{ $t('article.translitWords') }}
          </button>
        </template>
        <template #toolbar>
          <ToolbarWidget>
            <template #toggle v-if="enableAudio">
              <div class="toolbar-toggle-group">
                <SoundToggle v-if="enableAudio" v-model="soundModel"/>
              </div>
            </template>
            <template #stats>
              <WordStatsWidget
                  @dailyClick="navigateToDailyWords"
                  @collectionClick="() => viewModel.navigateToWordCollection()"
                  @repeatClick="() => viewModel.navigateToRepeat()"
              />
            </template>
          </ToolbarWidget>
        </template>
        <template #toolbarCompact>
          <ToolbarWidget>
            <template #toggle v-if="enableAudio">
              <div class="toolbar-toggle-group">
                <SoundToggle v-if="enableAudio" v-model="soundModel"/>
              </div>
            </template>
            <template #stats>
              <WordStatsWidget
                  :hide-today="true"
                  @dailyClick="navigateToDailyWords"
                  @collectionClick="() => viewModel.navigateToWordCollection()"
                  @repeatClick="() => viewModel.navigateToRepeat()"
              />
            </template>
          </ToolbarWidget>
        </template>
        <div class="news-feed">
          <transition name="feed-fade">
          <ArticlePageSkeleton v-if="state.isLoading" key="skeleton" />
          <div v-else key="content">
            <div style=" vertical-align: center; text-align: center">
              <div class="article-hero" v-if="state.article">
                <img :src="state.article.title.imageUrl || ''" class="hero-image" alt="news"/>
              </div>
              <div class="title" v-if="state.article">
                <TranslatedTextV2
                    :translationData="state.article.title.title"
                    :mode="state.mode"
                    :showButtons="false"
                    :transliterate="globalTransliterateEnabled"
                    :soundEnabled="enableAudio && soundEnabled"
                    :primaryTextClass="getArticleTitleClass(newsSourceLanguage)"
                    :secondaryTextClass="getArticleTitleClass(globalTransliterateEnabled ? newsSourceLanguage : newsTargetLanguage)"
                    @audio-auth-required="handleAudioAuthRequired"
                    @audio-paywall-required="handleAudioPaywallRequired"
                />
              </div>
              <div class="subtitle">
                {{ formattedArticleDate }}
              </div>

            </div>

            <main class="main-content" v-if="state.article">
              <div v-for="(block, blockIndex) in articleBlocks" :key="blockIndex" class="article-block">
                <div class="text-ctr">
                  <TranslatedTextV2
                      v-for="(c, sentenceIndex) in block"
                      :key="`${blockIndex}-${sentenceIndex}`"
                      :mode="state.mode"
                      :show-buttons="false"
                      :transliterate="globalTransliterateEnabled"
                      :soundEnabled="enableAudio && soundEnabled"
                      :translation-data="c"
                      :primaryTextClass="getTextClass(newsSourceLanguage)"
                      :secondaryTextClass="getTextClass(globalTransliterateEnabled ? newsSourceLanguage : newsTargetLanguage)"
                      @audio-auth-required="handleAudioAuthRequired"
                      @audio-paywall-required="handleAudioPaywallRequired"
                  />
                </div>
                <div class="inline-show-words" v-if="viewModel.canShowAllWords()">
                  <button
                      class="pill-btn pill-btn--outline-primary"
                      @click="openWordsPopup(blockIndex)"
                      :disabled="state.isLoadingMnemonics || state.isMnemonicPopupVisible"
                  >
                    {{ $t('article.showWords') }}
                  </button>
                </div>
                <div
                    v-if="viewModel.canShowAllWords() && blockIndex < articleBlocks.length - 1"
                    class="show-words-divider"
                    aria-hidden="true"
                ></div>
              </div>

              <!-- Bottom Navigation and Actions -->
              <div class="bottom-actions">
                <button
                    class="pill-btn pill-btn--outline-primary"
                    @click="viewModel.navigateToPrevious"
                    :disabled="!state.hasPrevious || state.isNavigatingToPrevious"
                >
                  {{ $t('article.previous') }}
                </button>
                <button
                    v-if="canPlayQuiz"
                    class="pill-btn pill-btn--outline-primary pill-btn--wrap quiz-btn"
                    @click="viewModel.startQuiz"
                >
                  {{ $t('article.playQuiz') }}
                </button>
                <button
                    class="pill-btn pill-btn--outline-primary"
                    @click="viewModel.navigateToNext"
                    :disabled="!state.hasNext || state.isNavigatingToNext"
                >
                  {{ $t('article.next') }}
                </button>
              </div>
            </main>
          </div>
          </transition>
        </div>
      </ContentArea>
    </transition>

    <!-- Word Details View -->
    <WordDetailsView/>
  </MainLayout>
</template>

<script setup lang="ts">
import {
  ArticlePageViewModel,
  Language,
  NewsCategoryStore,
  NewsTranslationStore,
  getNewsSourceLanguage,
  getNewsTargetLanguage
} from "@lingvo-news/contracts";
import TranslatedTextV2 from "../../components/TranslatedTextV2.vue";
import NewsHeader from "../../components/header/news-header.vue";
import NewsMenu from "../../components/header/news-menu.vue";
import ArticlePageSkeleton from "../../components/skeleton/ArticlePageSkeleton.vue";
import WordDetailsView from "../../components/WordDetailsView.vue";
import UserWidget from "../../components/header/UserWidget.vue";
import { useFontsLoadComplete } from "../../../composables/useLoadComplete";
import { getArticleTitleClass, getTextClass } from "../../styles/get-text-classes";
import MainLayout from "../../components/MainLayout.vue";
import ContentArea from "../../components/ContentArea.vue";
import { getVueEnv } from "../../../infrastructure/vue-env";
import { getCurrentUserWrapper } from "../../../infrastructure/current-user-wrapper";
import { serviceContainer } from '../../../store/service-container';
import { useCommonRouteParams } from '../../../composables/useCommonRouteParams';
import { computed, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import WordStatsWidget from "../../components/WordStatsWidget.vue";
import ToolbarWidget from "../../components/widgets/ToolbarWidget.vue";
import SoundToggle from "../../components/widgets/SoundToggle.vue";
import { formatUserLocalDate } from '../../../utils/format-user-local-date';
import { getCurrentUserStore } from '../../../store/services';
import { PaywallPopupViewModel } from '@lingvo-news/contracts';
import { isTelegramMiniApp } from '../../../infrastructure/auth/isTelegramMiniApp';
import { getWordAudioStatus, isWordAudioEnabled, setWordAudioEnabled } from '../../services/word-audio-service';
import { enableAudio } from '../../../constants';
import translateIconUrl from '../../../assets/translate.svg';

const env = getVueEnv();
const router = useRouter();
const routeParams = useCommonRouteParams();

function createViewModel() {
  const currentUser = getCurrentUserWrapper();
  const vm = new ArticlePageViewModel(
      env,
      currentUser,
      () => Promise.resolve(serviceContainer.get(NewsCategoryStore)),
      () => Promise.resolve(serviceContainer.get(NewsTranslationStore)),
      routeParams.nativeLanguage,
      routeParams.learnedLanguage,
      routeParams.mode
  );
  vm.useTelegramPayment = isTelegramMiniApp();
  return vm;
}

const TRANSLITERATE_STORAGE_KEY = 'lingvo-news-article-transliterate';

function loadTransliterateFromStorage(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage?.getItem(TRANSLITERATE_STORAGE_KEY) === '1';
}

function saveTransliterateToStorage(value: boolean): void {
  if (typeof window === 'undefined') return;
  window.localStorage?.setItem(TRANSLITERATE_STORAGE_KEY, value ? '1' : '0');
}

const viewModel = createViewModel();
const state = viewModel.state;
const globalTransliterateEnabled = ref<boolean>(loadTransliterateFromStorage());

function toggleGlobalTransliteration(): void {
  globalTransliterateEnabled.value = !globalTransliterateEnabled.value;
  saveTransliterateToStorage(globalTransliterateEnabled.value);
}

const hasTitleTransliteration = computed<boolean>(() => {
  const titleSentence = state.article?.title?.title;
  if (!titleSentence) return false;
  return titleSentence.words.some((word) => !!word.transliteration);
});

const hasAnyBlockTransliteration = computed<boolean>(() => {
  const blocks = viewModel.getArticleBlocks();
  return blocks.some((block) =>
    block.some((sentence) => sentence.words.some((word) => !!word.transliteration))
  );
});

const showHeaderTranslitToggle = computed<boolean>(() => {
  if (state.learnedLanguage !== Language.HY) return false;
  return hasTitleTransliteration.value || hasAnyBlockTransliteration.value;
});

watch(
  () => state.translationId,
  () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  },
  { immediate: true }
);

const newsSourceLanguage = computed(() => getNewsSourceLanguage({
  nativeLanguage: state.nativeLanguage,
  learnedLanguage: state.learnedLanguage,
  mode: state.mode
}));

const newsTargetLanguage = computed(() => getNewsTargetLanguage({
  nativeLanguage: state.nativeLanguage,
  learnedLanguage: state.learnedLanguage,
  mode: state.mode
}));

const soundEnabled = ref<boolean>(isWordAudioEnabled());

const soundModel = computed<boolean>({
  get: () => soundEnabled.value,
  set: (value) => {
    void handleSoundToggle(value);
  }
});

const setSoundState = (enabled: boolean): void => {
  soundEnabled.value = enabled;
  setWordAudioEnabled(enabled);
};

const showAudioPaywall = async (): Promise<void> => {
  await env.popupService.showPopup(new PaywallPopupViewModel({
      message: { stringId: 'errors.paywall.membershipRequired', formatParams: {} },
      buyMeCoffeeUrl: 'https://buymeacoffee.com/lingvo.news/membership',
      useTelegramPayment: isTelegramMiniApp(),
  }));
};

const handleSoundToggle = async (nextValue: boolean): Promise<void> => {
  if (nextValue===soundEnabled.value) {
    return;
  }

  if (!nextValue) {
    setSoundState(false);
    return;
  }

  const userStore = getCurrentUserStore();
  await userStore.initialize();
  if (!userStore.getCurrentUserId()) {
    await env.routing.navigate(env.routing.getLoginLink());
    return;
  }

  const status = await getWordAudioStatus();
  if (status.kind==='unauthorized') {
    await env.routing.navigate(env.routing.getLoginLink());
    return;
  }

  if (status.kind==='paywall') {
    setSoundState(false);
    await showAudioPaywall();
    return;
  }

  setSoundState(true);
};

const handleAudioAuthRequired = async (): Promise<void> => {
  setSoundState(false);
  await env.routing.navigate(env.routing.getLoginLink());
};

const handleAudioPaywallRequired = async (): Promise<void> => {
  setSoundState(false);
  await showAudioPaywall();
};

const formattedArticleDate = computed(() => formatUserLocalDate(state.article?.title.date, ''));
const articleCategoryId = computed(() => state.selectedCategoryId || state.article?.title.category || undefined);
const articleBlocks = computed(() => viewModel.getArticleBlocks());
const canPlayQuiz = computed(() => viewModel.canPlayQuiz());

const navigateToDailyWords = async (): Promise<void> => {
  await router.push({
    name: 'daily-words',
    params: {
      nativeLanguage: routeParams.nativeLanguage,
      learnedLanguage: routeParams.learnedLanguage,
      mode: routeParams.mode
    }
  });
};

async function openWordsPopup(partIndex: number): Promise<void> {
  await viewModel.openMnemonicPopupForPart(partIndex);
}

useFontsLoadComplete(async () => {
  await viewModel.onReady();
});
</script>

<style scoped lang="less">
@import "../../styles/text-styles.less";

.title {
  line-height: var(--line-height-title);
  padding-top: 15px;
}

.toolbar-toggle-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.continue-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.continue-button {
  min-width: 160px;
}

@media (max-width: 800px) {
  .title {
    font-size: var(--font-size-subheader);
    line-height: var(--line-height-title);
  }
}

/* Page-level fade (ContentArea enter/leave) */
.fade-enter-active { transition: opacity 0.25s ease; }
.fade-leave-active { transition: opacity 0.08s ease; }
.fade-enter-from,
.fade-leave-to { opacity: 0; }

/* Feed-level fade: skeleton ↔ article content */
.feed-fade-enter-active { transition: opacity 0.35s ease; }
.feed-fade-leave-active {
  transition: opacity 0.12s ease;
  position: absolute;
  top: 0; left: 0; right: 0;
  pointer-events: none;
}
.feed-fade-enter-from,
.feed-fade-leave-to { opacity: 0; }

.news-feed {
  color: #374151;
  border-radius: 4px;
  padding-bottom: var(--section-gap);
  position: relative; /* required for feed-fade absolute leave */
}

.category-menu {
  font-size: var(--font-size-header);
}

.article-hero {
  padding: 16px var(--content-padding-x) 0 var(--content-padding-x);
  display: flex;
  justify-content: center;
}

.hero-image {
  width: 380px;
  height: 220px;
  object-fit: cover;
  border-radius: 8px;
}

@media (max-width: 800px) {
  .hero-image {
    width: 100%;
    max-width: 320px;
    height: auto;
  }
}

.article-title {
  color: #ff2d2d;
  font-size: var(--font-size-header);
  line-height: var(--line-height-title);
  margin-bottom: 6px;
}

.subtitle {
  padding: var(--block-gap) var(--content-padding-x) var(--block-gap) var(--content-padding-x);
  font-size: var(--font-size-menu);
  color: #666;

  .source {
    background: #f0f0f0;
    padding: 2px 6px;
    border-radius: 8px;
    color: #333;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

@media (max-width: 800px) {
  .subtitle {
    font-size: var(--font-size-caption);
    padding: var(--block-gap) var(--content-padding-x);
  }
}

.text-ctr {
  margin: 20px auto;
  line-height: var(--line-height-regular);
  padding-top: 24px;
  position: relative;
}

.article-block + .article-block {
  margin-top: var(--block-gap);
}

.inline-show-words {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 6px 0 12px 0;
}

.show-words-divider {
  width: min(680px, 100%);
  height: 1px;
  margin: 2px auto 14px auto;
  background: #d7dce3;
}

@media (max-width: 800px) {
  .text-ctr {
    line-height: var(--line-height-regular);
    padding-top: 16px;
    margin: 16px auto;
  }

  .show-words-divider {
    margin: 0 auto 10px auto;
  }
}

.translit-icon {
  flex-shrink: 0;
  opacity: 0.7;
}

.pill-btn.active .translit-icon {
  opacity: 1;
}

.bottom-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 40px 0 60px 0;
}

.bottom-actions .quiz-btn {
  width: 250px;
  flex: 0 0 250px;
}

@media (max-width: 800px) {
  .bottom-actions {
    gap: 8px;
    padding: 26px 0 40px 0;
  }

  .bottom-actions .pill-btn {
    flex: 1 1 0;
    min-width: 0;
    justify-content: center;
  }
}

/* Phone-only layout: keep tablet/desktop unchanged */
@media (max-width: 576px) {
  .article-hero {
    display: none;
  }

  .subtitle {
    display: none;
  }
}

/* Article page: keep WordStatsWidget left-aligned (don't push it to the right) */
:deep(.content-area .header-section) {
  justify-content: flex-start;
  flex-wrap: wrap;
}

@media (max-width: 800px) {
  :deep(.content-area .header-section) {
    justify-content: center;
    row-gap: 12px;
  }

  :deep(.content-area .news-list-title) {
    width: 100%;
  }

  :deep(.content-area .toolbar-anchor) {
    width: 100%;
    display: flex;
    justify-content: center;
  }
}
</style>


